import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { useTranslation } from '@hooks';
import { KNOWLEDGE_BASE_TYPE_LABELS, KnowledgeBaseType } from '@constants/knowledge-base';

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({ className, onApplyFilters, disabled = false, filter, setFilter }) => {
    const [refetchFilters, setRefetchFilters] = useState(false);
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const { t } = useTranslation();
    const agents = useSelector(state => state.agents.data);

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            onApplyFilters();
        }
    }, [refetchFilters]);

    const options = useMemo(() => {
        return {
            agents: agents.map(agent => ({ value: agent.id, label: agent.name, name: agent.name })),
            types: Object.values(KnowledgeBaseType).map(type => {
                const label = t(KNOWLEDGE_BASE_TYPE_LABELS[type]);
                return { value: type, label, name: label };
            }),
        };
    }, [agents, t]);

    const update = {
        agents: items => {
            setFilter(prev => ({ ...prev, agents: items }));
        },
        types: items => {
            setFilter(prev => ({ ...prev, types: items }));
        },
    };

    const formOptions = [
        {
            type: 'multiSelect',
            label: t('KnowledgeBase.agents'),
            value: filter.agents,
            onChange: update.agents,
            options: options.agents,
            countable: true,
            nameBased: true,
            style: { gridColumn: '1 / -1' },
        },
        {
            type: 'multiSelect',
            label: t('common.type'),
            value: filter.types,
            onChange: update.types,
            options: options.types,
            countable: true,
            nameBased: true,
            style: { gridColumn: '1 / -1' },
        },
    ];

    const activeFilters = [filter.agents, filter.types].filter(values => values.length > 0).length;

    const resetFilters = () => setFilter(prev => ({ ...prev, agents: [], types: [] }));

    return (
        <FiltersWrapper>
            <Button
                className={className}
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filterNumber > 0 ? ` (${filterNumber})` : ''}
            </Button>
            <FiltersModal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setFilterNumber(activeFilters);
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                onClear={resetFilters}
                clearDisabled={activeFilters === 0}
            >
                <FiltersFormCreator type="knowledge-base" options={formOptions} />
            </FiltersModal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
